import { OrganType } from '@/api/apis/case';
import { TMDXFile } from '@/api/apis/files';
import { OrganDataByOrgan } from './organData';

type UploadFiles = (files: TMDXFile[]) => Promise<TMDXFile[]>;

const isPending = (file: TMDXFile) => !!file.file;

export const uploadPendingDocuments = async (
  documents: TMDXFile[] | undefined,
  uploadFiles: UploadFiles,
  onResolved?: (documents: TMDXFile[]) => void,
): Promise<TMDXFile[] | undefined> => {
  const pending = documents?.filter(isPending) ?? [];

  if (!pending.length) {
    return documents;
  }

  const uploaded = [...(await uploadFiles(pending))];

  const resolved = documents!.map((document) => (isPending(document) ? (uploaded.shift() ?? document) : document));

  onResolved?.(resolved);

  return resolved;
};

/** CT Scan docs are per organ — several slices can hold pending uploads. */
export const uploadPendingOrganDocuments = async (
  organData: OrganDataByOrgan | undefined,
  uploadFiles: UploadFiles,
  onResolved?: (organType: OrganType, documents: TMDXFile[]) => void,
): Promise<OrganDataByOrgan> => {
  const entries = Object.entries(organData ?? {}) as [OrganType, OrganDataByOrgan[OrganType]][];

  const uploaded = await Promise.all(
    entries.map(async ([organType, values]) => {
      const ctScanDocuments = await uploadPendingDocuments(values?.ctScanDocuments, uploadFiles, (documents) =>
        onResolved?.(organType, documents),
      );
      return [organType, { ...values, ctScanDocuments }] as const;
    }),
  );

  return Object.fromEntries(uploaded);
};
